import React, { Component, ErrorInfo, ReactNode } from 'react';
import styled from 'styled-components';
import { GRUNDFOS_BLUE, BLUE3, ATTENTION_BLUE, BLUE1 } from '../../../constants/colors';
import { FONT_FAMILY, FONT_SIZE } from '../../../constants/fonts';

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

const Fallback = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 100vh;
  padding: 32px;
  text-align: center;
  background: ${BLUE1};
`;

const Fallback__Title = styled.h1`
  color: ${GRUNDFOS_BLUE};
  font-family: ${FONT_FAMILY.BOLD};
  font-size: 28px;
  margin: 0 0 12px 0;
`;

const Fallback__Message = styled.p`
  color: ${BLUE3};
  font-family: ${FONT_FAMILY.REGULAR};
  font-size: ${FONT_SIZE.MEDIUM};
  max-width: 480px;
  line-height: 1.5;
  margin: 0 0 24px 0;
`;

const Fallback__Button = styled.button`
  padding: 12px 28px;
  border: none;
  border-radius: 12px;
  background: ${ATTENTION_BLUE};
  color: white;
  font-family: ${FONT_FAMILY.SEMI_BOLD};
  font-size: ${FONT_SIZE.MEDIUM};
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(18, 106, 243, 0.3);
  }
`;

export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    console.error('[ErrorBoundary] Uncaught rendering error:', error, info.componentStack);
  }

  private handleReload = () => {
    window.location.reload();
  };

  render(): ReactNode {
    if (this.state.hasError) {
      return (
        <Fallback>
          <Fallback__Title>Something went wrong</Fallback__Title>
          <Fallback__Message>
            An unexpected error occurred. Please reload the page to continue.
          </Fallback__Message>
          <Fallback__Button onClick={this.handleReload}>
            Reload page
          </Fallback__Button>
        </Fallback>
      );
    }

    return this.props.children;
  }
}
